/**
 * Product Labels Module
 *
 * Keeps the launch-price badge in sync with Shopify.
 *
 * The badge is server-rendered from the "Einführungspreis" tag that the shop
 * sync writes into the product frontmatter, so it paints on first load and
 * works without JS. This module then tops that up from the live catalog (the
 * same cache the stock and discount badges read), so a tag added in Shopify
 * shows up without waiting for a re-sync and rebuild.
 *
 * It only ever adds. The catalog is cached for up to 60 minutes, so it can be
 * older than the page it is correcting — trusting it to remove would delete a
 * correct badge whenever a visitor's cache predates the tag. Removal therefore
 * waits for the next sync and deploy, same as the price.
 *
 * The add path does open a window the server-rendered badge does not have: the
 * tag is read live, but the price beside it is whatever the last sync wrote. A
 * merchant who tags a product and changes its price in the same edit gets the
 * badge immediately and the new price only after the next deploy. In practice
 * an introductory price is set when the product is created and synced, and the
 * tag is added against an already-correct price, so the two line up — but the
 * badge is not proof that the price next to it has caught up.
 */

import { getProductTags } from "./stock-cache.js";

const LAUNCH_TAG = "Einführungspreis";
const DETAIL_CLASS = "shop-product__launch-badge";
const CARD_CLASS = "shop-card__launch-badge";

function hasLaunchTag(tags) {
  return (tags || []).includes(LAUNCH_TAG);
}

/** Badge copy for the current page language. */
function labelText() {
  const lang = (window.VANYX_CONFIG || {}).lang || "de";
  return lang === "en" ? "Launch price" : "Einführungspreis";
}

/**
 * Add the badge if the live tags call for one and the template did not already
 * render it. It is appended, so on a card it lands under the stock badge in the
 * badge column, matching where the template puts it.
 */
function addIfMissing(root, className) {
  if (!root || root.querySelector("." + className)) {
    return;
  }
  const badge = document.createElement("span");
  badge.className = className;
  badge.textContent = labelText();
  root.appendChild(badge);
}

export async function init() {
  const tags = await getProductTags();
  if (!tags) {
    return;
  }

  const product = document.querySelector("[data-shopify-handle]");
  const priceRow = document.querySelector(".shop-product__price-row");
  if (product && priceRow && hasLaunchTag(tags[product.dataset.shopifyHandle])) {
    addIfMissing(priceRow, DETAIL_CLASS);
  }

  document.querySelectorAll("[data-product-handle]").forEach((card) => {
    if (hasLaunchTag(tags[card.dataset.productHandle])) {
      addIfMissing(card.querySelector(".shop-card__badges"), CARD_CLASS);
    }
  });
}
